import Service from '@ember/service';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import ENV from 'tio-employee/config/environment';
import { generateRequestId } from 'tio-common/utils/request-id';
import { timeZone } from 'tio-common/utils/date/time-zone';
import type SessionService from 'tio-employee/services/session';

type RequirementsResponse = {
  requires?: string[];
  temp_token?: string;
  post_login_redirect?: string | null;
};

export default class PostLoginService extends Service {
  @service declare session: SessionService;

  @tracked requires: string[] = [];
  @tracked tempToken?: string;
  @tracked postLoginRedirect?: string | null;
  @tracked mfaCodeSent = false;

  initializeFromSession() {
    const data = this.session.data.authenticated as RequirementsResponse | undefined;
    if (!data) return;
    if (Array.isArray(data.requires)) {
      this.requires = data.requires;
    }
    if (typeof data.temp_token === 'string') {
      this.tempToken = data.temp_token;
    }
    if (typeof data.post_login_redirect === 'string' || data.post_login_redirect === null) {
      this.postLoginRedirect = data.post_login_redirect;
    }
  }

  get hasRequirements() {
    return this.requires.length > 0;
  }

  get nextRequirement() {
    return this.requires[0];
  }

  get nextRouteName() {
    switch (this.nextRequirement) {
      case 'mfa_sms':
        return 'post-login.mfa-sms';
      case 'terms_and_conditions':
        return 'post-login.terms';
      default:
        return null;
    }
  }

  async refreshRequirements() {
    const response = await this.request<RequirementsResponse>('/api/v2/post_login/requirements', {
      method: 'GET',
    });
    this.requires = response.requires || [];
    this.postLoginRedirect = response.post_login_redirect ?? null;
    return response;
  }

  async acceptTerms() {
    const response = await this.request<RequirementsResponse>(
      '/api/v2/post_login/terms_acceptance',
      {
        method: 'POST',
        body: JSON.stringify({}),
      }
    );
    this.requires = response.requires || [];
    this.postLoginRedirect = response.post_login_redirect ?? null;
    return response;
  }

  async complete() {
    const response = await this.request<{ access_token: string }>('/api/v2/post_login/complete', {
      method: 'POST',
      body: JSON.stringify({}),
    });
    return response.access_token;
  }

  async verifyMfa(code: string) {
    const response = await this.request<{ access_token: string }>('/api/v2/mfa/verify', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
    return response.access_token;
  }

  async completeAuthentication(accessToken: string) {
    const wasAuthenticated = this.session.isAuthenticated;
    await this.session.authenticate('authenticator:saml', { access_token: accessToken });
    if (wasAuthenticated) {
      this.session.handleAuthentication('authenticated');
    }
    this.clear();
  }

  async resendMfa() {
    await this.request('/api/v2/mfa/resend', {
      method: 'POST',
      body: JSON.stringify({}),
    });
    this.mfaCodeSent = true;
  }

  async ensureMfaCodeSent() {
    if (this.mfaCodeSent) return;

    try {
      await this.resendMfa();
    } catch (error) {
      console.error('[post-login] ensureMfaCodeSent failed', error);
    }
  }

  clear() {
    this.requires = [];
    this.tempToken = undefined;
    this.postLoginRedirect = null;
    this.mfaCodeSent = false;
  }

  private async request<T = unknown>(path: string, init: RequestInit): Promise<T> {
    if (!this.tempToken) {
      this.initializeFromSession();
    }

    if (!this.tempToken) {
      throw new Error('Missing temp token');
    }

    const response = await fetch(`${ENV.apiHost}${path}`, {
      ...init,
      headers: {
        'x-api-key': ENV.apiKey,
        'tio-temp-token': this.tempToken,
        'Content-Type': 'application/json',
        'Time-Zone': timeZone(),
        'X-Request-Id': generateRequestId(),
      },
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error((errorBody as { error?: string }).error || response.statusText);
    }

    if (response.status === 204) {
      return {} as T;
    }

    return (await response.json()) as T;
  }
}
